import { formatDate } from '../../../lib/date'
import { useAuthStore } from '../../../stores/auth'

export type MembershipStatusType = 'active' | 'expired' | 'cancelled' | 'paused' | 'expiringSoon' | 'noMembership'

export interface MembershipStatus {
  type: MembershipStatusType
  message: string
  isExpired: boolean
  isExpiringSoon: boolean
  isCancelled: boolean
  isPaused: boolean
  showSelectPlan: boolean
  showAlert: boolean
}

export function getMembershipStatus(): MembershipStatus {
  const { user } = useAuthStore()
  const isMember = !!user?.roles?.includes('member')

  // Not a member role: no alerts
  if (!isMember) {
    return {
      type: 'active',
      message: '',
      isExpired: false,
      isExpiringSoon: false,
      isCancelled: false,
      isPaused: false,
      showSelectPlan: false,
      showAlert: false,
    }
  }

  const membershipStatus = user?.membership?.status
  const endDateRaw = user?.membership?.end_date
  const endDate = endDateRaw ? new Date(endDateRaw) : null
  const today = new Date()

  // Normalize to start of day for comparison
  const normalizeDate = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const todayStart = normalizeDate(today)

  // Handle CANCELLED status
  if (membershipStatus === 'cancelled') {
    return {
      type: 'cancelled',
      message: 'Your membership has been cancelled. Select a new plan to continue.',
      isExpired: false,
      isExpiringSoon: false,
      isCancelled: true,
      isPaused: false,
      showSelectPlan: true,
      showAlert: true,
    }
  }

  // Handle PAUSED status
  if (membershipStatus === 'paused') {
    return {
      type: 'paused',
      message: 'Your membership has been paused. Please contact support for more information.',
      isExpired: false,
      isExpiringSoon: false,
      isCancelled: false,
      isPaused: true,
      showSelectPlan: false,
      showAlert: true,
    }
  }

  // Handle EXPIRED status or past end date
  if (membershipStatus === 'expired' || (endDate && normalizeDate(endDate) < todayStart)) {
    return {
      type: 'expired',
      message: 'Your membership has expired. Select a plan to continue.',
      isExpired: true,
      isExpiringSoon: false,
      isCancelled: false,
      isPaused: false,
      showSelectPlan: true,
      showAlert: true,
    }
  }

  // Handle ACTIVE status with end date
  if (membershipStatus === 'active' && endDate) {
    const endDateStart = normalizeDate(endDate)

    // Check if expiring within 7 days (including today)
    const inSevenDays = new Date(todayStart.getTime() + 7 * 24 * 60 * 60 * 1000)
    if (endDateStart <= inSevenDays) {
      return {
        type: 'expiringSoon',
        message: `Your membership will expire on ${formatDate(endDateStart)}.`,
        isExpired: false,
        isExpiringSoon: true,
        isCancelled: false,
        isPaused: false,
        showSelectPlan: false,
        showAlert: true,
      }
    }
  }

  // No membership or active with no concerns
  if (!user?.membership) {
    return {
      type: 'noMembership',
      message: "You don't have an active membership. Select a plan to get started.",
      isExpired: false,
      isExpiringSoon: false,
      isCancelled: false,
      isPaused: false,
      showSelectPlan: true,
      showAlert: true,
    }
  }

  // Active membership with no concerns
  return {
    type: 'active',
    message: '',
    isExpired: false,
    isExpiringSoon: false,
    isCancelled: false,
    isPaused: false,
    showSelectPlan: false,
    showAlert: false,
  }
}
